import React, { useState } from 'react';
import { DollarSignIcon, InfoIcon, BookIcon, CalendarIcon } from 'lucide-react';
import { Budget } from './BudgetManagement';
import { BudgetStatusBadge } from './BudgetStatusBadge';
import { BudgetRules } from './BudgetRules';
import { formatCurrency } from '../../utils/currencyFormatter';
interface BudgetCardProps {
  budget: Budget;
  locale: string;
}
export function BudgetCard({
  budget,
  locale
}: BudgetCardProps) {
  // Calculate percentage of budget used
  const percentUsed = Math.min(100, Math.round((budget.totalAmount - budget.remainingAmount) / budget.totalAmount * 100));
  const percentRemaining = 100 - percentUsed;
  // Determine color based on remaining percentage
  const getProgressColor = () => {
    if (percentRemaining > 50) return 'bg-green-500';
    if (percentRemaining > 20) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  // Format dates
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  return <div className="bg-white rounded-lg overflow-hidden" data-id="element-6599">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-6600">
        <div className="md:col-span-2" data-id="element-6601">
          <div className="space-y-4" data-id="element-6602">
            <div data-id="element-6603">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-6604">
                {budget.name}
              </h4>
              {budget.description && <p className="mt-1 text-sm text-gray-500" data-id="element-6605">
                  {budget.description}
                </p>}
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-6606">
              <div data-id="element-6607">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-6608">
                  <BookIcon className="h-3 w-3 mr-1" data-id="element-6609" />
                  Budget Type
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-6610">
                  {budget.budgetTypeName}
                </p>
              </div>
              <div data-id="element-6611">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-6612">
                  <CalendarIcon className="h-3 w-3 mr-1" data-id="element-6613" />
                  Period
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-6614">
                  {formatDate(budget.startDate)} to {formatDate(budget.endDate)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-6615">
              <div data-id="element-6616">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-6617">
                  GL Code
                </h5>
                <p className="mt-1 text-sm font-mono text-gray-900" data-id="element-6618">
                  {budget.glCode}
                </p>
              </div>
              <div data-id="element-6619">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-6620">
                  Entity / Site
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-6621">
                  {budget.entityName || 'Global'}
                  {budget.siteName && <span className="text-gray-500" data-id="element-6622"> / {budget.siteName}</span>}
                </p>
              </div>
            </div>
            <div data-id="element-6623">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-6624">
                <DollarSignIcon className="h-3 w-3 mr-1" data-id="element-6625" />
                Budget Status
              </h5>
              <div className="mt-2 flex items-center" data-id="element-6626">
                <BudgetStatusBadge status={budget.status} data-id="element-6627" />
                <span className="ml-2 text-sm text-gray-500" data-id="element-6628">
                  Last updated:{' '}
                  {new Date(budget.updatedAt).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="md:col-span-1" data-id="element-6629">
          <div className="space-y-4" data-id="element-6630">
            <div data-id="element-6631">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-6632">
                Budget Amount
              </h5>
              <div className="mt-1" data-id="element-6633">
                <div className="text-2xl font-semibold text-gray-900" data-id="element-6634">
                  {formatCurrency(budget.remainingAmount, budget.currency, locale)}
                </div>
                <div className="text-sm text-gray-500" data-id="element-6635">
                  remaining of{' '}
                  {formatCurrency(budget.totalAmount, budget.currency, locale)}
                </div>
              </div>
            </div>
            <div data-id="element-6636">
              <div className="flex justify-between text-sm" data-id="element-6637">
                <span data-id="element-6638">{percentRemaining}% remaining</span>
                <span data-id="element-6639">{percentUsed}% used</span>
              </div>
              <div className="mt-1 w-full bg-gray-200 rounded-full h-2.5" data-id="element-6640">
                <div className={`h-2.5 rounded-full ${getProgressColor()}`} style={{
                width: `${percentRemaining}%`
              }} data-id="element-6641"></div>
              </div>
            </div>
            <div className="mt-4" data-id="element-6642">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-6643">
                <InfoIcon className="h-3 w-3 mr-1" data-id="element-6644" />
                Budget Rules
              </h5>
              <div className="mt-2" data-id="element-6645">
                <BudgetRules rulesJson={budget.rules} data-id="element-6646" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}